/**
 * /field/ew/jobs/:id/log/:workDate — the hand's Earthworks daily log for a
 * day (GET / PUT /earthworks/field/jobs/:id/daily-logs/:workDate,
 * fieldiq-api#1284). An upsert by day: the same Save writes it the first time
 * and updates it after. SNAKE_CASE both ways.
 *
 * CREW ONLY (403 not_on_crew, said as a sentence). A unit with registered
 * equipment offers "File a pre-use check": the shared equipment check flow,
 * which files the check and brings the hand back here (`returnTo`), where the
 * check shows in the unit's picker to link.
 *
 * Warnings show at the top of the form with a "Carry on" tap each; they never
 * disable Save. A save with a `warnings` entry (meter_below_previous) went
 * through and says so in amber.
 */
import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { useEwMeta } from '../../../hooks/useEarthworks';
import { useEwFieldJob, useEwFieldLog, useEwPreUseChecks, useSaveEwFieldLog } from '../../../hooks/useEwField';
import { ewApiError, ewDay, ewMeterWarningText, ewRefusal } from '../../../lib/earthworks';
import type { EwFieldLogSaved } from '../../../types/earthworks';
import { PtFieldCard, PtFieldPage, PtFieldQueryState, PtRefusal } from '../../pressure-testing/field/PtFieldBits';
import { fromFieldWarning } from '../EwWarnings';
import { EwLogForm } from './EwLogForm';
import { ewNotOnCrewMessage } from './EwFieldJobScreen';

export default function EwFieldLogScreen() {
  const { id, workDate } = useParams<{ id: string; workDate: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const jobQuery = useEwFieldJob(id);
  const logQuery = useEwFieldLog(id, workDate);
  const checks = useEwPreUseChecks(id, workDate);
  const catalog = useEwMeta().data?.data ?? null;
  const save = useSaveEwFieldLog();
  const [error, setError] = useState<string | null>(null);
  const [meterWarnings, setMeterWarnings] = useState<EwFieldLogSaved['warnings']>([]);
  const [formKey, setFormKey] = useState(0);

  const job = jobQuery.data?.data ?? null;
  const log = logQuery.data?.data ?? null;
  const meta = logQuery.data?.meta ?? jobQuery.data?.meta ?? null;
  const warnings = (logQuery.data?.dayWarnings ?? []).map(fromFieldWarning);
  const refused = ewNotOnCrewMessage(jobQuery) ?? ewNotOnCrewMessage(logQuery);
  const loggable = job?.status === 'scheduled' || job?.status === 'in_progress';
  const unitName = (equipmentId?: string) => job?.equipment.find((e) => e.equipment_id === equipmentId)?.equipment_name ?? null;

  return (
    <PtFieldPage title={`Daily log · ${ewDay(workDate)}`} backTo={id ? `/field/ew/jobs/${id}` : '/field/ew'} testId="ew-field-log">
      {refused ? (
        <PtRefusal message={refused} testId="ew-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={jobQuery} subject="this job">
          <PtFieldQueryState query={logQuery} subject="this daily log">
            {!job ? (
              <PtRefusal message="That job no longer exists. It may have been deleted." />
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-fieldiq-text">
                  <span className="font-mono font-extrabold">{job.job_number}</span> · {job.customer_name || '—'} · {job.site_name || '—'}
                </p>
                {meterWarnings.length > 0 && (
                  <div role="status" data-testid="ew-log-meter-warnings" className="flex gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-amber" aria-hidden="true" />
                    <ul className="space-y-1">
                      {meterWarnings.map((w, i) => (
                        <li key={i}>{ewMeterWarningText(w, unitName(w.equipment_id))}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {!loggable && (
                  <PtRefusal
                    message={
                      job.status === 'draft'
                        ? 'This job is still a draft. The office schedules it before work is logged.'
                        : 'This job is completed or cancelled. Its logs can no longer be changed.'
                    }
                  />
                )}
                <PtFieldCard className="px-4 py-4">
                  <EwLogForm
                    key={`${log?.id ?? 'new'}-${formKey}`}
                    jobId={job.id}
                    workDate={workDate ?? ''}
                    job={job}
                    log={log}
                    catalog={catalog}
                    meta={meta}
                    checks={checks.data?.data ?? []}
                    warnings={warnings}
                    userId={user?.id ?? null}
                    pending={save.isPending}
                    error={error}
                    readOnly={!loggable}
                    submitLabel={log ? 'Save log' : 'Save today’s log'}
                    fileCheckState={(u) => ({
                      to: '/field/equipment/check',
                      state: { equipment: { id: u.equipmentId, name: u.name, unit_number: u.detail }, returnTo: location.pathname },
                    })}
                    onSubmit={(body) => {
                      setError(null);
                      save.mutate(
                        { jobId: job.id, day: workDate as string, body },
                        {
                          onSuccess: (res) => {
                            toast.success('Daily log saved');
                            setMeterWarnings(res.warnings);
                            // The write's invalidation has re-read the log by now, so rebuild the form from it.
                            setFormKey((k) => k + 1);
                          },
                          onError: (err) =>
                            setError(
                              ewApiError(err).status === null
                                ? 'The daily log was NOT saved — no answer from the server. Check your signal and save again.'
                                : ewRefusal(err, 'The daily log could not be saved.'),
                            ),
                        },
                      );
                    }}
                  />
                </PtFieldCard>
              </div>
            )}
          </PtFieldQueryState>
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
